import { useEffect, useRef } from 'react';
import { usePlayerStore } from '@/state/playerStore';
import { YouTubeTransport } from './youtubeTransport';
import { embedUrl, YouTubePlayer } from './ytPlayer';
import styles from './YouTubeDock.module.css';

/**
 * The song's video in the player. It is the sound source for YouTube songs: once the embedded
 * player answers, it becomes the player's transport (controls, views and shortcuts follow it).
 */
export function YouTubeDock({ videoId, duration }: { videoId: string; duration: number }) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const attachVideo = usePlayerStore((s) => s.attachVideo);
  const detachVideo = usePlayerStore((s) => s.detachVideo);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const player = new YouTubePlayer(frame);
    const transport = new YouTubeTransport(player, duration);
    player.onReady = () => void attachVideo(transport);
    return () => {
      detachVideo(transport);
      transport.dispose();
      player.dispose();
    };
  }, [videoId, duration, attachVideo, detachVideo]);

  return (
    <div className={styles.dock}>
      <iframe
        ref={frameRef}
        src={embedUrl(videoId)}
        title="YouTube video"
        // YouTube refuses embeds without a referrer (error 153); the site's no-referrer policy
        // is relaxed for this frame only, and only the origin is sent.
        referrerPolicy="strict-origin-when-cross-origin"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}
